import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import Editor from 'ckeditor5-custom-build/build/ckeditor'
import CKEditor from '@ckeditor/ckeditor5-react'
import styled from 'styled-components'
import { Tooltip } from 'antd'
import { SmileOutlined } from '@ant-design/icons'
import { EMOJI_CATEGORIES } from '../../constants/newsFeedConstants'
import { apiUrl } from '../../../../config'
import utils from '../../../../utils'

const EditorContainer = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  position: relative;

  &:focus-within {
    border-color: #1877f2;
    box-shadow: 0 0 0 2px rgba(24, 119, 242, 0.12);
  }

  /* CKEditor Toolbar resets */
  .ck.ck-toolbar {
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
  }

  .ck.ck-toolbar .ck-toolbar__items {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-wrap: wrap;
  }

  .ck.ck-button {
    min-height: 28px !important;
    height: 28px !important;
    padding: 0 6px !important;
    border-radius: 6px !important;
    cursor: pointer !important;
    color: #475569 !important;
    border: 1px solid transparent !important;
    transition: all 0.12s ease !important;

    &:hover:not(.ck-disabled) {
      background: #f1f5f9 !important;
      color: #1e293b !important;
    }

    &.ck-on {
      background: #e0f2fe !important;
      color: #0284c7 !important;
      border-color: #bae6fd !important;
      font-weight: 700 !important;
    }

    &.ck-disabled {
      opacity: 0.35 !important;
    }
  }

  .ck.ck-toolbar__separator {
    background: #e2e8f0 !important;
    height: 18px !important;
    margin: 0 4px !important;
  }

  /* CKEditor Content Area */
  .ck-content {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    padding: 12px 14px !important;
    font-size: 15px !important;
    line-height: 1.6 !important;
    font-family: inherit !important;
    color: #050505 !important;
    min-height: ${props => props.minHeight || '120px'} !important;
    max-height: ${props => props.maxHeight || '320px'} !important;
    overflow-y: auto !important;
    scrollbar-gutter: stable;

    /* Custom slim scrollbar */
    &::-webkit-scrollbar {
      width: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;
    }

    p {
      margin: 0 0 6px 0 !important;
      &:last-child {
        margin-bottom: 0 !important;
      }
    }

    /* Poem verse styling - preserve line rhythm */
    blockquote {
      border-left: 3px solid #1877f2 !important;
      background: #f0f7ff !important;
      padding: 6px 12px !important;
      margin: 6px 0 !important;
      font-style: italic !important;
      border-radius: 0 6px 6px 0 !important;
    }

    ul, ol {
      padding-left: 20px !important;
      margin: 4px 0 8px 0 !important;
    }

    a {
      color: #1877f2 !important;
      text-decoration: none !important;
      &:hover {
        text-decoration: underline !important;
      }
    }

    /* Mentions inside editor */
    .mention {
      color: #1877f2 !important;
      font-weight: 600 !important;
      background: rgba(24, 119, 242, 0.08) !important;
      border-radius: 4px !important;
      padding: 1px 4px !important;
      text-decoration: none !important;
    }

    /* Placeholder */
    &.ck-placeholder::before {
      color: #94a3b8 !important;
      font-style: normal !important;
      left: 14px !important;
    }
  }
`

const HeaderBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  background: #ffffff;
  border-bottom: 1px solid #f1f5f9;
  padding: 6px 10px;
  border-radius: 11px 11px 0 0;
  position: relative;
  user-select: none;
`

const ToolbarSlot = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`

const ActionsSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  position: relative;
  padding-top: 2px;
`

const EmojiActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: #f59e0b;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.15s ease;

  &:hover {
    background: #fef3c7;
    border-color: #fde68a;
  }

  &.active {
    background: #fef3c7;
    border-color: #f59e0b;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const EmojiDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 12px 32px -4px rgba(0, 0, 0, 0.16),
    0 4px 12px -2px rgba(0, 0, 0, 0.08);
  width: 440px;
  max-width: min(440px, calc(100vw - 32px));
  z-index: 1100;
  padding: 12px;
  animation: fadeIn 0.15s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const EmojiTabs = styled.div`
  display: flex;
  gap: 6px;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 10px;
  margin-bottom: 10px;
`

const CategoryTab = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 13px;
  white-space: nowrap;
  border-radius: 8px;
  border: none;
  background: ${props => (props.active ? '#eff6ff' : 'transparent')};
  color: ${props => (props.active ? '#1877f2' : '#64748b')};
  font-weight: ${props => (props.active ? '600' : '500')};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => (props.active ? '#eff6ff' : '#f1f5f9')};
    color: ${props => (props.active ? '#1877f2' : '#1e293b')};
  }
`

const EmojiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 4px;
  max-height: 220px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px 6px 2px 2px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`

const EmojiCell = styled.div`
  height: 36px;
  font-size: 22px;
  text-align: center;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.12s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f1f5f9;
    transform: scale(1.18);
  }

  &:active {
    transform: scale(0.95);
  }
`

// Bộ công cụ rút gọn hợp lý cho mạng xã hội News Feed
const NEWS_FEED_TOOLBAR_ITEMS = [
  'bold',
  'italic',
  'underline',
  'strikethrough',
  '|',
  'alignment',
  '|',
  'bulletedList',
  'numberedList',
  '|',
  'link',
  'blockQuote',
  '|',
  'undo',
  'redo',
]

const NewsFeedEditor = ({
  value = '',
  onChange,
  placeholder = 'Bạn muốn chia sẻ điều gì thế?',
  minHeight = '120px',
  maxHeight = '320px',
  disabled = false,
  userStore,
  onReady,
}) => {
  const editorRef = useRef(null)
  const toolbarContainerRef = useRef(null)
  const actionsSlotRef = useRef(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [activeEmojiTab, setActiveEmojiTab] = useState('smileys')

  // Đóng emoji picker khi click ngoài
  useEffect(() => {
    if (!showEmojiPicker) return
    const handleClickOutside = e => {
      if (actionsSlotRef.current && !actionsSlotRef.current.contains(e.target)) {
        setShowEmojiPicker(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showEmojiPicker])

  // Chèn emoji vào vị trí con trỏ của CKEditor
  const handleInsertEmoji = useCallback(emoji => {
    const editor = editorRef.current
    if (editor) {
      editor.model.change(writer => {
        const insertPosition = editor.model.document.selection.getFirstPosition()
        editor.model.insertContent(writer.createText(emoji), insertPosition)
      })
      editor.editing.view.focus()
    }
  }, [])

  // Nguồn dữ liệu gắn thẻ (@mention) cho CKEditor
  const getMentionFeed = useCallback(
    queryText => {
      const isItemMatching = item => {
        const searchString = queryText.toLowerCase()
        const display =
          item.display ||
          item.fullName ||
          item.full_name ||
          item.name_uppercase ||
          ''
        const username = item.username || item.code || item.userCode || ''
        return (
          display.toLowerCase().includes(searchString) ||
          username.toLowerCase().includes(searchString)
        )
      }

      return new Promise(resolve => {
        const allUsers = [
          ...(userStore?.mentionUserList || []),
          ...(userStore?.userList || []),
        ]

        const seen = new Set()
        const uniqueUsers = []
        for (const u of allUsers) {
          const key = u.username || u.code || u.id
          if (key && !seen.has(key)) {
            seen.add(key)
            uniqueUsers.push(u)
          }
        }

        const itemsToDisplay = uniqueUsers
          .filter(isItemMatching)
          .slice(0, 10)
          .map(item => {
            const rawName =
              item.display ||
              item.fullName ||
              item.full_name ||
              item.name_uppercase ||
              item.username
            const name = utils.getNameInCapitalize(rawName)
            return {
              id: `@${name}`,
              userId: item.id || item.username,
              name: name,
              image: item.image || item.imageId || item.userImage,
              username: item.username,
            }
          })

        resolve(itemsToDisplay)
      })
    },
    [userStore]
  )

  const customItemRenderer = useCallback(item => {
    const itemElement = document.createElement('span')
    itemElement.classList.add('custom-item')
    itemElement.style.display = 'flex'
    itemElement.style.alignItems = 'center'
    itemElement.style.gap = '8px'
    itemElement.style.padding = '4px 6px'

    const avatar = document.createElement('span')
    avatar.style.width = '24px'
    avatar.style.height = '24px'
    avatar.style.borderRadius = '50%'
    avatar.style.display = 'inline-flex'
    avatar.style.alignItems = 'center'
    avatar.style.justifyContent = 'center'
    avatar.style.background = '#1877f2'
    avatar.style.color = '#fff'
    avatar.style.fontSize = '12px'
    avatar.style.fontWeight = 'bold'
    avatar.style.overflow = 'hidden'
    avatar.style.flexShrink = '0'

    if (item.image) {
      const img = document.createElement('img')
      img.src = item.image.startsWith('http')
        ? item.image
        : `${apiUrl}/api/v1/images/${item.image}`
      img.style.width = '100%'
      img.style.height = '100%'
      img.style.objectFit = 'cover'
      avatar.appendChild(img)
    } else {
      avatar.textContent = (item.name || 'U').charAt(0).toUpperCase()
    }

    const nameElement = document.createElement('span')
    nameElement.textContent = item.name
    nameElement.style.fontWeight = '600'
    nameElement.style.fontSize = '13px'
    nameElement.style.color = '#050505'

    itemElement.appendChild(avatar)
    itemElement.appendChild(nameElement)

    return itemElement
  }, [])

  const currentCategory = useMemo(() => {
    return (
      EMOJI_CATEGORIES.find(c => c.key === activeEmojiTab) ||
      EMOJI_CATEGORIES[0]
    )
  }, [activeEmojiTab])

  return (
    <EditorContainer
      minHeight={minHeight}
      maxHeight={maxHeight}
      className="newsfeed-editor-container">
      {/* Header bar: CKEditor Toolbar + Emoji Picker */}
      <HeaderBar>
        <ToolbarSlot ref={toolbarContainerRef} />
        <ActionsSlot ref={actionsSlotRef}>
          <Tooltip title="Chèn biểu tượng cảm xúc">
            <EmojiActionBtn
              type="button"
              disabled={disabled}
              className={showEmojiPicker ? 'active' : ''}
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>😊</span>
            </EmojiActionBtn>
          </Tooltip>

          {showEmojiPicker && (
            <EmojiDropdown onClick={e => e.stopPropagation()}>
              <EmojiTabs>
                {EMOJI_CATEGORIES.map(cat => (
                  <CategoryTab
                    key={cat.key}
                    active={activeEmojiTab === cat.key}
                    onClick={() => setActiveEmojiTab(cat.key)}>
                    <span>{cat.icon}</span>
                    <span>{cat.name}</span>
                  </CategoryTab>
                ))}
              </EmojiTabs>
              <EmojiGrid>
                {currentCategory.emojis.map((emoji, index) => (
                  <EmojiCell
                    key={`${emoji}-${index}`}
                    onClick={() => handleInsertEmoji(emoji)}
                    title={emoji}>
                    {emoji}
                  </EmojiCell>
                ))}
              </EmojiGrid>
            </EmojiDropdown>
          )}
        </ActionsSlot>
      </HeaderBar>

      {/* CKEditor Decoupled Editor */}
      <CKEditor
        editor={Editor}
        data={value}
        disabled={disabled}
        config={{
          toolbar: {
            items: NEWS_FEED_TOOLBAR_ITEMS,
            shouldNotGroupWhenFull: true,
          },
          placeholder: placeholder,
          link: {
            addTargetToExternalLinks: true,
          },
          mention: {
            feeds: [
              {
                marker: '@',
                feed: getMentionFeed,
                minimumCharacters: 1,
                itemRenderer: customItemRenderer,
              },
            ],
          },
        }}
        onInit={editor => {
          editorRef.current = editor

          // Đưa thanh toolbar vào ToolbarSlot phía trên
          if (toolbarContainerRef.current) {
            toolbarContainerRef.current.innerHTML = ''
            toolbarContainerRef.current.appendChild(
              editor.ui.view.toolbar.element
            )
          }

          // Convert up-down cast cho mentions
          try {
            editor.conversion.for('upcast').elementToAttribute({
              view: {
                name: 'a',
                key: 'data-mention',
                classes: 'mention',
                attributes: {
                  href: true,
                  'data-user-id': true,
                },
              },
              model: {
                key: 'mention',
                value: viewItem => {
                  return editor.plugins
                    .get('Mention')
                    .toMentionAttribute(viewItem, {
                      link: viewItem.getAttribute('href'),
                      userId: viewItem.getAttribute('data-user-id'),
                    })
                },
              },
              converterPriority: 'high',
            })

            editor.conversion.for('downcast').attributeToElement({
              model: 'mention',
              view: (modelAttributeValue, { writer }) => {
                if (!modelAttributeValue) return
                return writer.createAttributeElement(
                  'a',
                  {
                    class: 'mention',
                    'data-mention': modelAttributeValue.id,
                    'data-user-id': modelAttributeValue.userId,
                    href: '#',
                  },
                  {
                    priority: 20,
                    id: modelAttributeValue.uid,
                  }
                )
              },
              converterPriority: 'high',
            })
          } catch (e) {
            // Mention plugin handles defaults
          }

          if (onReady) {
            onReady(editor)
          }
        }}
        onChange={(event, editor) => {
          const data = editor.getData()
          if (onChange) {
            onChange(data)
          }
        }}
      />
    </EditorContainer>
  )

}

export default NewsFeedEditor
